import { handleApiError } from "utils/errorHandler";
import { getAuthCookie } from "../../utils/authCookies";

export type SpecialFee = {
    id: string;
    name: string;
    amount: number;
    taxable: boolean;
    available_on: string[]; // e.g. ["checkoutPage"]
    required: boolean;
    status: boolean;
    limit_to_agents: string[]; // userIds
    limit_to_brokerages: string[]; // brokerageIds (can be same id domain as user)
    limit_to_groups: string[]; // group ids
};


export type TaxCloudAddress = {
    addressLine1: string;
    city: string;
    state: string; // 2-letter code preferred
    zip: string;
};
export type UiFee = {
    id: string;
    title: string;
    subtitle?: string;
    amount: number;
    required?: boolean;
    note?: string;
    icon?: string;
    // extra fields (safe for TS structural typing; UI can ignore them)
    taxable?: boolean;
    noteTitle?: string;
    noteText?: string;
};

export type CartLineInput = {
    itemId: string;
    price: number;
    quantity: number;
    title?: string;
    sku?: string;
    shipping_name?: string;
    itemType: "default" | "shipping" | "special_fee" | "service";
};



export function toTaxCloudAddressFromObject(obj: any): TaxCloudAddress | null {
    if (!obj) return null;
    // try common shapes
    const a = obj.address_details || obj.address || obj || obj.shippingAddress;

    // if (!a) return null;
    const addressLine1 = a.addressLine1 || a.line1 || a.address1 || "";
    const city = a.city || a.cityCode || "";
    const state = a.stateCode || a.state || "";
    const zip = a.zip || a.postalCode || a.postcode || "";
    // console.log(addressLine1, city, state, zip, "toTaxCloudAddressFromObject12233");
    // if (!addressLine1 || !city || !state || !zip) return null;
    return { addressLine1, city, state, zip };
}

function addressFromTaxCloudOrigin(origin: any): TaxCloudAddress | null {
    if (!origin) return null;
    return {
        addressLine1: origin.addressLine1 || "",
        city: origin.city || origin.cityCode || "",
        state: origin.stateCode || origin.state || "",
        zip: origin.zip || "",
    };
}

// export async function fetchDefaultWarehouseAddress(token: string, supplierId?: string): Promise<TaxCloudAddress | null> {
//     const userId = getAuthCookie("userId");
//     const licenseType = getAuthCookie("licenseType");
//     const userName = getAuthCookie("userName");
//     try {
//         const currentSupplierId = supplierId || localStorage.getItem("supplierId") || "";
//         console.log("installationmandremovaltaxcloudapicalls inside taxcloud/carts/created-by", currentSupplierId);
//         if (!currentSupplierId) return null;
//         console.log("installationmandremovaltaxcloudapicalls inside taxcloud/carts/created-by data fetchiing success");
//         const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${currentSupplierId}`, {
//             method: "GET",
//             headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         });

//         if (!res.ok) return null;

//         const data = await res.json();
//         const configs: any[] = Array.isArray(data) ? data : [];

//         // Get the first config's origin_address
//         const config = configs.find((c) => c?.origin_address) || configs[0];
//         const originAddress = config?.origin_address;

//         return originAddress ? addressFromTaxCloudOrigin(originAddress) : null;
//     } catch (error) {
//         const currentSupplierId = supplierId || localStorage.getItem("supplierId") || "";
//         await handleApiError(error, {
//             module: "other",
//             action: "InstallationandRemovalTaxcloud_fetch_default_warehouse_address",
//             userId: userId || undefined,
//             licenseType: licenseType || undefined,
//             userName: userName || undefined,
//             url: `${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${currentSupplierId}`,
//             method: "GET"
//         }, token);
//         return null;
//     }
// }



export async function pingTaxCloud(token: string, supplierId?: string): Promise<boolean> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    if (!supplierId) return false;
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        return res.ok;
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_ping_tax_cloud",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`,
            method: "GET"
        }, token);
        return false;
    }
}



export function buildTaxableFeeLines(selectedFees: UiFee[]): CartLineInput[] {
    return (selectedFees || [])
        .filter(f => f && f.taxable && Number(f.amount) > 0)
        .map(f => ({
            itemId: f.title,
            price: Number(f.amount),
            quantity: 1,
            itemType: "special_fee" as const,
        }));
}

// --- 3) Upsert with product + shipping + many fee lines --------------
export async function upsertTaxCloudCartMulti(params: {
    token: string;
    userId: string;
    cartId?: string;
    supplierId?: string;
    destination: TaxCloudAddress;
    products: { sku: string; price: number; quantity: number; itemType?: "default" | "shipping" | "special_fee" | "service" }[];
    shipping?: { id?: string; cost?: number; title?: string } | null;
    taxableFees?: UiFee[];  // already filtered to the ones you selected; we'll auto-drop non-taxable here too
    originOverride?: TaxCloudAddress | null;
}) {
    const { token, userId, destination, products, shipping, taxableFees, originOverride, cartId, supplierId } = params;
    // console.log("11111111111111", params)
    // const origin = originOverride || (await fetchDefaultWarehouseAddress(token, supplierId));
    // if (!origin) throw new Error("No Taxcloud address found");



    const productLines: CartLineInput[] = products.map(p => ({
        itemId: p.sku,
        price: Number(p.price || 0),
        quantity: Number(p.quantity || 1),
        itemType: p.itemType || "service",  // ✅ Preserve itemType from input, default to "service"
    }));

    const shippingLine: CartLineInput[] =
        shipping && Number(shipping.cost) > 0
            ? [{ itemId: shipping.title || shipping.id || "SHIP001", price: Number(shipping.cost || 0), quantity: 1, itemType: "special_fee" }]
            : [];

    const feeLines = buildTaxableFeeLines(taxableFees || []);

    return upsertTaxCloudCart({
        token,
        cartId: cartId || "",
        customerId: userId,
        supplierId: supplierId || "",
        // origin :  undefined,
        destination,
        cartItems: [...productLines, ...shippingLine, ...feeLines],
    });
}

// --- 4) Orders API (commit -> complete -> refund) --------------------
export async function createTaxCloudOrder(token: string, cartId: string, orderId: string, supplierId?: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = { cartId, orderId };
    try {
        // console.log(token, cartId, orderId,"this is ok")
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        // console.log(res, "resokzsszszss")
        if (!res.ok) throw new Error("TaxCloud order creation failed");
        return res.json().catch(() => ({}));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_create_tax_cloud_order",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`,
            method: "POST",
            requestBody: payload
        }, token);
        throw error;
    }
}

export async function completeTaxCloudOrder(token: string, orderId: string, completedDateISO: string, supplierId?: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = { completedDate: completedDateISO };
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/complete/created-by/${supplierId}`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("TaxCloud order complete failed");
        return res.json().catch(() => ({}));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_complete_tax_cloud_order",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/complete/created-by/${supplierId}`,
            method: "PATCH",
            requestBody: payload,
            additionalInfo: {
                orderId: orderId || null
            }
        }, token);
        throw error;
    }
}


export async function Refund(token: string, order_number: string): Promise<boolean> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = {};
    if (!token || !order_number) {
        console.warn("[TaxCloud] Refund skipped: missing token or order number");
        return false;
    }

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${order_number}/refund`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        // console.log("[TaxCloud] refund response", res);
        return res.ok;
    } catch (error) {
        console.error("[TaxCloud] refund failed", error);
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_refund",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/${order_number}/refund`,
            method: "POST",
            requestBody: payload,
            additionalInfo: {
                orderNumber: order_number || null
            }
        }, token);
        return false;
    }
}


export async function PostVersionOrdernumber(token: string, ordernumber: string,order_type: string): Promise<boolean> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = {order_type: order_type, order_number: ordernumber };
    if (!token || !ordernumber) {
        console.warn("[TaxCloud] Refund skipped: missing token or order number");
        return false;
    }

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud_order/version`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        // console.log("[TaxCloud] refund response", res);
        return res.ok;
    } catch (error) {
        console.error("[TaxCloud] refund failed", error);
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_post_version_order_number",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud_order/version`,
            method: "POST",
            requestBody: payload,
            additionalInfo: {
                orderNumber: ordernumber || null,
                orderType: order_type || null
            }
        }, token);
        return false;
    }
}

export async function GetVersionOrdernumber(
    token: string,
    ordernumber: string,
    order_type: string
): Promise<any | null> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    if (!ordernumber) return null;
    try {
        const params = new URLSearchParams({
            order_type: order_type || "installation",
            order_number: ordernumber,
        });
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud_order/version?${params.toString()}`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        return data;
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_get_version_order_number",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud_order/version?order_type=${order_type || "installation"}&order_number=${ordernumber}`,
            method: "GET",
            additionalInfo: {
                orderNumber: ordernumber || null,
                orderType: order_type || null
            }
        }, token);
        return null;
    }
}


export async function refundTaxCloudOrder(token: string, orderId: string, supplierId?: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = {};
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund/created-by/${supplierId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("TaxCloud refund failed");
        return res.json().catch(() => ({}));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_refund_tax_cloud_order",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund/created-by/${supplierId}`,
            method: "POST",
            requestBody: payload,
            additionalInfo: {
                orderId: orderId || null
            }
        }, token);
        throw error;
    }
}


export async function upsertTaxCloudCart(params: {
    token: string;
    cartId: string;
    customerId: string;
    // origin: TaxCloudAddress;
    supplierId?: string;
    destination: TaxCloudAddress;
    cartItems: CartLineInput[];
}): Promise<any> {
    const { token, cartId, customerId,destination, cartItems, supplierId } = params;
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = { cartId, customerId, destination, cartItems };
    // console.log("upsertTaxCloudCarcheckert", cartId, customerId, origin, destination, cartItems);
    //  console.log("salesordertaxcloudapicalls inside taxcloud/carts/created-by", supplierId);
    if (!supplierId) return null;
    // console.log("salesordertaxcloudapicalls inside taxcloud/carts/created-by data fetchiing success");  
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const raw = await res.text();
        let json: any = null;
        try {
            json = raw ? JSON.parse(raw) : null;
        } catch {
            json = null;
        }

        if (!res.ok) {
            const msg = json?.message || json?.error || raw || "Upsert cart failed";
            throw new Error(msg);
        }
        return json;
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "InstallationandRemovalTaxcloud_upsert_tax_cloud_cart",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`,
            method: "POST",
            requestBody: payload
        }, token);
        throw error;
    }
}